import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, LogOut, FileText, Users, Send, FileEdit, Download, X, ArrowLeft, Files, Star, FileSpreadsheet, Save, UserPlus, ShieldCheck, Trash2, Copy, SlidersHorizontal } from "lucide-react";
import api, { FILE_BASE } from "../api";
import { formSections } from "../mock";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import { useToast } from "../hooks/use-toast";

const LABELS = (() => {
  const m = {};
  formSections.forEach((s) => s.fields.forEach((f) => { m[f.name] = { label: f.label, section: s.title, type: f.type }; }));
  return m;
})();

const REVIEW = {
  new: { label: "New", color: "#9ca3af" },
  reviewed: { label: "Reviewed", color: "#60a5fa" },
  shortlisted: { label: "Shortlisted", color: "#8cff00" },
  passed: { label: "Passed", color: "#f87171" },
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="hss-card p-5 flex items-center gap-4">
    <span className="flex items-center justify-center rounded-2xl" style={{ width: 46, height: 46, background: `${color}1f`, border: `1px solid ${color}55` }}>
      <Icon size={22} style={{ color }} />
    </span>
    <div>
      <div className="font-display font-black text-2xl text-white">{value}</div>
      <div className="kicker text-white/45" style={{ fontSize: "0.6rem", letterSpacing: "0.15em" }}>{label}</div>
    </div>
  </div>
);

const fmt = (d) => { try { return new Date(d).toLocaleString(); } catch { return "-"; } };

const ext = (name = "") => name.split(".").pop().toLowerCase();
const kindOf = (f) => {
  const c = (f.content_type || "").toLowerCase();
  const e = ext(f.filename);
  if (c.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(e)) return "image";
  if (c.startsWith("audio/") || ["mp3", "wav", "m4a", "aac", "ogg", "flac"].includes(e)) return "audio";
  if (c.startsWith("video/") || ["mp4", "mov", "webm", "mkv", "avi"].includes(e)) return "video";
  return "other";
};

const FilePreview = ({ f }) => {
  const src = `${FILE_BASE}/${f.file_id}`;
  const kind = kindOf(f);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-white text-sm truncate">{f.filename}</p>
          <p className="text-white/40 text-xs">{LABELS[f.field]?.label || f.field} · {(f.size / 1024 / 1024).toFixed(2)} MB</p>
        </div>
        <a href={src} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm font-semibold shrink-0" style={{ color: "#8cff00" }}>
          <Download size={16} /> Download
        </a>
      </div>
      {kind === "image" && <img src={src} alt={f.filename} className="w-full max-h-72 object-contain rounded-lg bg-black/40" />}
      {kind === "audio" && <audio controls preload="none" src={src} className="w-full mt-1" />}
      {kind === "video" && <video controls preload="none" src={src} className="w-full max-h-80 rounded-lg bg-black/40" />}
    </div>
  );
};

const Detail = ({ sub, onClose, onUpdated }) => {
  const { toast } = useToast();
  const [status, setStatus] = useState(sub.review_status || "new");
  const [notes, setNotes] = useState(sub.review_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);

  const setReview = async (newStatus) => {
    setStatus(newStatus);
    try {
      await api.patch(`/admin/submissions/${sub.submission_id}/review`, { status: newStatus });
      toast({ title: "Status updated", description: `Marked as ${REVIEW[newStatus].label}.` });
      onUpdated && onUpdated();
    } catch (e) { toast({ title: "Could not update status" }); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/admin/submissions/${sub.submission_id}/review`, { notes });
      toast({ title: "Notes saved" });
      onUpdated && onUpdated();
    } catch (e) { toast({ title: "Could not save notes" }); }
    finally { setSavingNotes(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative h-full w-full max-w-2xl overflow-y-auto bg-[#0a0713] border-l border-white/10 p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="kicker" style={{ color: "#d8b4fe" }}>{sub.status === "submitted" ? "Submitted" : "Draft"}</p>
            <h3 className="font-ui font-extrabold text-white text-2xl mt-1">{sub.data?.artistName || sub.artist_name || "Unnamed artist"}</h3>
            <p className="text-white/50 text-sm">{sub.artist_email}</p>
            <p className="text-white/35 text-xs mt-1">Updated {fmt(sub.updated_at)}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={22} /></button>
        </div>

        {/* Review controls */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 mb-6">
          <p className="font-ui font-bold text-white text-xs uppercase tracking-[0.18em] mb-3">Review</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(REVIEW).map(([key, v]) => (
              <button key={key} onClick={() => setReview(key)}
                className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-all"
                style={{
                  background: status === key ? `${v.color}22` : "rgba(255,255,255,0.04)",
                  border: `1px solid ${status === key ? v.color : "rgba(255,255,255,0.12)"}`,
                  color: status === key ? v.color : "#b9b5c6",
                }}>
                {key === "shortlisted" && <Star size={14} />} {v.label}
              </button>
            ))}
          </div>
          <label className="text-white/45 text-xs">Private notes (only your team can see these)</label>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="hss-input mt-2" placeholder="Add internal notes about this artist…" />
          <div className="flex justify-end mt-3">
            <button onClick={saveNotes} disabled={savingNotes} className="btn-green-outline">
              {savingNotes ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save notes
            </button>
          </div>
        </div>

        {sub.files?.length > 0 && (
          <div className="mb-6">
            <h4 className="font-ui font-bold text-white text-xs uppercase tracking-wider mb-3">Uploaded files</h4>
            <div className="space-y-3">
              {sub.files.map((f) => <FilePreview key={f.file_id} f={f} />)}
            </div>
          </div>
        )}

        {formSections.map((s) => {
          const answered = s.fields.filter((f) => {
            const v = sub.data?.[f.name];
            return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && String(v).trim() !== "";
          });
          if (answered.length === 0) return null;
          return (
            <div key={s.title} className="mb-6">
              <h4 className="font-ui font-bold text-[#8cff00] text-xs uppercase tracking-[0.18em] mb-3">{s.title}</h4>
              <div className="space-y-3">
                {answered.map((f) => {
                  let v = sub.data[f.name];
                  if (Array.isArray(v)) v = v.join(", ");
                  return (
                    <div key={f.name} className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
                      <p className="text-white/45 text-xs">{f.label}</p>
                      <p className="text-white text-sm mt-1 whitespace-pre-wrap break-words">{String(v)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TeamPanel = () => {
  const { toast } = useToast();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [granting, setGranting] = useState(false);
  const [tempCred, setTempCred] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/team"); setTeam(data); } catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grant = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setGranting(true); setTempCred(null);
    try {
      const { data } = await api.post("/admin/team/grant", { email: email.trim(), name: name.trim() || undefined });
      if (data.created && data.temp_password) {
        setTempCred({ email: data.user.email, password: data.temp_password });
        toast({ title: "Admin created", description: "Share the temporary password below." });
      } else {
        toast({ title: "Admin access granted", description: `${data.user.email} is now an admin.` });
      }
      setEmail(""); setName(""); load();
    } catch (err) {
      toast({ title: "Could not grant access", description: err?.response?.data?.detail || "Please try again." });
    } finally { setGranting(false); }
  };

  const revoke = async (u) => {
    if (!window.confirm(`Remove admin access for ${u.email}?`)) return;
    try {
      await api.post("/admin/team/revoke", { user_id: u.user_id });
      toast({ title: "Access revoked", description: `${u.email} is no longer an admin.` });
      load();
    } catch (err) {
      toast({ title: "Could not revoke", description: err?.response?.data?.detail || "Please try again." });
    }
  };

  const copyCred = () => {
    navigator.clipboard?.writeText(`Email: ${tempCred.email}\nPassword: ${tempCred.password}`);
    toast({ title: "Copied", description: "Login details copied to clipboard." });
  };

  return (
    <div className="mt-12">
      <div className="flex items-center gap-2 mb-5">
        <ShieldCheck size={22} style={{ color: "#d8b4fe" }} />
        <h2 className="font-ui font-extrabold uppercase text-white text-2xl">Team Management</h2>
      </div>

      <div className="hss-card p-6 mb-5">
        <p className="font-ui font-bold text-white text-sm mb-1">Grant admin access</p>
        <p className="text-white/50 text-sm mb-4">Enter a team member's email. If they don't have an account yet, one is created and you'll get a temporary password to share.</p>
        <form onSubmit={grant} className="flex flex-col sm:flex-row gap-3">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@email.com" className="hss-input flex-1" />
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="hss-input sm:w-56" />
          <button type="submit" disabled={granting} className="btn-green whitespace-nowrap">
            {granting ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Grant admin
          </button>
        </form>
        {tempCred && (
          <div className="mt-4 rounded-xl border border-[#8cff00]/40 bg-[#8cff00]/10 p-4">
            <p className="text-white text-sm font-semibold mb-2">New admin account created — share these one-time login details:</p>
            <div className="flex items-center justify-between gap-3 rounded-lg bg-black/40 px-3 py-2">
              <code className="text-white/85 text-sm break-all">{tempCred.email} &nbsp;·&nbsp; {tempCred.password}</code>
              <button onClick={copyCred} className="flex items-center gap-1.5 text-sm font-semibold shrink-0" style={{ color: "#8cff00" }}><Copy size={14} /> Copy</button>
            </div>
            <p className="text-white/40 text-xs mt-2">They can change this later via the account menu → Change password.</p>
          </div>
        )}
      </div>

      <div className="hss-card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin" style={{ color: "#8cff00" }} size={26} /></div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="text-white/40 text-xs uppercase tracking-wider border-b border-white/10">
                <th className="px-5 py-4 font-semibold">Name</th>
                <th className="px-5 py-4 font-semibold">Email</th>
                <th className="px-5 py-4 font-semibold">Role</th>
                <th className="px-5 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {team.map((u) => (
                <tr key={u.user_id} className="border-b border-white/5">
                  <td className="px-5 py-4 text-white font-medium">{u.name}</td>
                  <td className="px-5 py-4 text-white/60 text-sm">{u.email}</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full"
                      style={{ background: u.role === "owner" ? "rgba(216,180,254,0.18)" : "rgba(96,165,250,0.15)", color: u.role === "owner" ? "#d8b4fe" : "#60a5fa" }}>
                      {u.role === "owner" && <ShieldCheck size={12} />}{u.role}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    {u.role === "admin" ? (
                      <button onClick={() => revoke(u)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-300 hover:text-red-200">
                        <Trash2 size={15} /> Revoke
                      </button>
                    ) : (<span className="text-white/30 text-xs">Owner</span>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const [{ data: st }, { data: list }] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/submissions", { params }),
      ]);
      setStats(st); setSubs(list);
    } catch (e) {} finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const openDetail = async (submission_id) => {
    try { const { data } = await api.get(`/admin/submissions/${submission_id}`); setSelected(data); } catch (e) {}
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/submissions/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `hss-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast({ title: "Export ready", description: "Your CSV download has started." });
    } catch (e) { toast({ title: "Export failed", description: "Please try again." }); }
    finally { setExporting(false); }
  };

  const handleLogout = async () => { await logout(); navigate("/first-season-artist-info"); };
  const filledCount = (sub) => Object.keys(sub.data || {}).filter((k) => !k.endsWith("__id")).length;

  return (
    <div className="min-h-screen bg-black">
      <header className="border-b border-white/10 bg-[#070410]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo height={40} />
            <span className="hidden sm:inline text-white/30">|</span>
            <span className="hidden sm:inline font-ui font-bold text-white uppercase tracking-wider text-sm">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/first-season-artist-info")} className="hidden sm:inline-flex items-center gap-2 text-white/60 hover:text-white text-sm"><ArrowLeft size={16} /> Site</button>
            <button onClick={() => navigate("/admin/questionnaire")} data-testid="edit-questionnaire-btn" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm"><SlidersHorizontal size={16} /> <span className="hidden sm:inline">Edit Questionnaire</span></button>
            <span className="text-white/60 text-sm hidden sm:inline">{user?.name}</span>
            <button onClick={handleLogout} className="btn-green-outline"><LogOut size={16} /> Log out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <h1 className="font-ui font-extrabold uppercase text-white text-3xl">Submissions</h1>
          <button onClick={exportCsv} disabled={exporting} className="btn-green">
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />} Export CSV
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
            <StatCard icon={FileText} label="Total" value={stats.total} color="#d8b4fe" />
            <StatCard icon={Send} label="Submitted" value={stats.submitted} color="#8cff00" />
            <StatCard icon={FileEdit} label="Drafts" value={stats.drafts} color="#fbbf24" />
            <StatCard icon={Star} label="Shortlisted" value={stats.shortlisted ?? 0} color="#a3e635" />
            <StatCard icon={Users} label="Artists" value={stats.artists} color="#60a5fa" />
            <StatCard icon={Files} label="Files" value={stats.files} color="#f472b6" />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by artist name or email…" className="hss-input" style={{ paddingLeft: "2.6rem" }} />
          </div>
          <div className="flex gap-2">
            {["all", "submitted", "draft"].map((s) => (
              <button key={s} onClick={() => setStatus(s)}
                className="px-4 py-2.5 rounded-full text-sm font-semibold capitalize transition-colors"
                style={{ background: status === s ? "#8cff00" : "rgba(255,255,255,0.05)", color: status === s ? "#000" : "#b9b5c6", border: "1px solid rgba(255,255,255,0.1)" }}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="hss-card overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: "#8cff00" }} size={30} /></div>
          ) : subs.length === 0 ? (
            <div className="text-center py-16 text-white/45">No submissions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-white/40 text-xs uppercase tracking-wider border-b border-white/10">
                    <th className="px-5 py-4 font-semibold">Artist</th>
                    <th className="px-5 py-4 font-semibold hidden md:table-cell">Email</th>
                    <th className="px-5 py-4 font-semibold">Status</th>
                    <th className="px-5 py-4 font-semibold">Review</th>
                    <th className="px-5 py-4 font-semibold hidden lg:table-cell">Fields</th>
                    <th className="px-5 py-4 font-semibold hidden lg:table-cell">Files</th>
                    <th className="px-5 py-4 font-semibold hidden md:table-cell">Updated</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => {
                    const rv = REVIEW[s.review_status || "new"];
                    return (
                      <tr key={s.submission_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors cursor-pointer" onClick={() => openDetail(s.submission_id)}>
                        <td className="px-5 py-4 text-white font-medium">{s.data?.artistName || s.artist_name || "—"}</td>
                        <td className="px-5 py-4 text-white/60 text-sm hidden md:table-cell">{s.artist_email}</td>
                        <td className="px-5 py-4">
                          <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: s.status === "submitted" ? "rgba(140,255,0,0.15)" : "rgba(251,191,36,0.15)", color: s.status === "submitted" ? "#8cff00" : "#fbbf24" }}>
                            {s.status}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: `${rv.color}1f`, color: rv.color, border: `1px solid ${rv.color}55` }}>
                            {(s.review_status || "new") === "shortlisted" && <Star size={11} />}{rv.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-white/60 hidden lg:table-cell">{filledCount(s)}</td>
                        <td className="px-5 py-4 text-white/60 hidden lg:table-cell">{s.files?.length || 0}</td>
                        <td className="px-5 py-4 text-white/50 text-sm hidden md:table-cell">{fmt(s.updated_at)}</td>
                        <td className="px-5 py-4 text-right"><span className="text-sm font-semibold" style={{ color: "#8cff00" }}>View</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {user?.role === "owner" && <TeamPanel />}
      </main>

      {selected && <Detail sub={selected} onClose={() => setSelected(null)} onUpdated={load} />}
    </div>
  );
};

export default AdminDashboard;
